import { Provider } from "react-redux"
import "./styles/cart.css"
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom"
import { store } from "./store"
import RestaurantApp from "./components/RestaurantApp"
import PaymentReturnHandler from "./components/PaymentReturnHandler"
import HomePage from "./features/menu/HomePage"
import CartScreen from "./features/cart/CartScreen"
import CheckoutScreen from "./features/orders/CheckoutScreen"
import SuccessScreen from "./features/orders/SuccessScreen"
import ErrorScreen from "./features/ui/ErrorScreen"
import NoInternetScreen from "./features/ui/NoInternetScreen"
import InvoiceExample from "./components/InvoiceExample"
import SimpleInvoiceExample from "./components/SimpleInvoiceExample"
import SuccessScreenWithDownload from "./components/SuccessScreenWithDownload"
import CleanInvoiceExample from "./components/CleanInvoiceExample"
import QRScannerDisplay from "./components/QRScannerDisplay"

import { ThemeProvider, useTheme } from "./contexts/ThemeContext"
import useTranslation from "./hooks/useTranslation"
import { useFavicon } from "./utils/useFavicon"
import { useDocumentTitle } from "./utils/useDocumentTitle"
import { setNavigate } from "./utils/navigation"
import { useDynamicColors } from "./hooks/useDynamicColors"
import { useEffect } from "react"
import { useLocation } from "react-router-dom"

function AppContent() {
  const { isRTL } = useTranslation();
  const { favIcon, restaurantName } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const windowLocation = window.location;
  
  // Helper: check params
  function hasMenuParams(search: string) {
    const params = new URLSearchParams(search);
    return !!(params.get('branchId') && params.get('typeId'));
  }
  
  useDynamicColors();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  useFavicon(favIcon);
  useDocumentTitle(restaurantName);
  
  // Fix double slashes in URL pathname
  useEffect(() => {
    const pathname = location.pathname;
    // Check if pathname contains double slashes (but not at the start after protocol)
    if (pathname.includes('//')) {
      // Remove all consecutive slashes and replace with single slash
      // Then ensure it starts with exactly one slash
      const cleanPath = pathname.replace(/\/+/g, '/');
      // Build new URL with clean pathname
      const newUrl = cleanPath + location.search;
      // Navigate to clean URL if it's different
      if (cleanPath !== pathname) {
        navigate(newUrl, { replace: true });
        return;
      }
    }
  }, [location.pathname, location.search, navigate]);
  
  // Redirect logic: if root and params available, go to /home
  useEffect(() => {
    if (location.pathname === '/' && hasMenuParams(location.search)) {
      navigate('/home' + location.search, { replace: true });
    }
  }, [navigate, location.pathname, location.search]);
  return (
    <div className="min-h-screen bg-gray-100 w-full" dir={isRTL ? "rtl" : "ltr"}>
      <Routes>
        <Route path="/" element={<QRScannerDisplay />} />
        <Route path="/home" element={<RestaurantApp><HomePage /></RestaurantApp>} />
        <Route path="/cart" element={<RestaurantApp><CartScreen /></RestaurantApp>} />
        <Route path="/checkout" element={<RestaurantApp><CheckoutScreen /></RestaurantApp>} />
        <Route path="/order-success" element={<RestaurantApp><SuccessScreen /></RestaurantApp>} />
        <Route path="/error" element={<RestaurantApp><ErrorScreen /></RestaurantApp>} />
        <Route path="/no-internet" element={<RestaurantApp><NoInternetScreen /></RestaurantApp>} />
        <Route path="/payment/return" element={<PaymentReturnHandler />} />
        <Route path="/invoice" element={<InvoiceExample />} />
        <Route path="/simple-invoice" element={<SimpleInvoiceExample />} />
        <Route path="/success-with-download" element={<SuccessScreenWithDownload />} />
        <Route path="/clean-invoice" element={<CleanInvoiceExample />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ThemeProvider>
    </Provider>
  );
}

export default App;